import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  Radio,
  RadioGroup,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getBackendURL, getSubRoutePath, apiFetch } from '../../../utils/utils';
import { useToast } from '@chakra-ui/react';

function MerchantUsersPage() {
  const toast = useToast();

  const markMerchantAsPaid = (id, txnId) => {
    setInProgress(true);
    apiFetch(`${getBackendURL()}/${getSubRoutePath(user)}/merchant/merchants/mark-as-paid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, txnId, ownerId: user._id }),
    })
      .then(res => res.json())
      .then(res => {
        setInProgress(false);
        fetchUsers();
        toast({ title: 'Success', description: res.message, status: 'success', duration: 3000 });
      })
      .catch(err => {
        setInProgress(false);
        console.error(err);
      });
  };
  const [resendConfirmUser, setResendConfirmUser] = useState(null);
  const [resendInProgress, setResendInProgress] = useState(false);
  const resendCancelRef = useRef();

  const openResendConfirm = (user) => {
    setResendConfirmUser(user);
  };

  const confirmResendMerchantPaymentLink = () => {
    if (!resendConfirmUser) return;
    setResendInProgress(true);
    apiFetch(`${getBackendURL()}/${getSubRoutePath(user)}/merchant/merchants/resend-payment-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: resendConfirmUser.id }),
    })
      .then(res => res.json())
      .then(res => {
        setResendInProgress(false);
        setResendConfirmUser(null);
        fetchUsers();
        toast({ title: 'Success', description: res.message, status: 'success', duration: 3000 });
      })
      .catch(err => {
        setResendInProgress(false);
        setResendConfirmUser(null);
        console.error(err);
        toast({ title: 'Error', description: 'Failed to resend payment link', status: 'error', duration: 3000 });
      });
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);

  const user = useSelector(state => state.userState.user);
  const [selectedUser, setSelectedUser] = useState({});
  const [error, setError] = useState('');
  const [isRequestCompleted, setIsRequestCompleted] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [actionStatus, setActionStatus] = useState('');
  const [isStatusUpdateRequest, setIsStatusUpdateRequest] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [paymentMethod, setPaymentMethod] = useState('OFFLINE');
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  const fetchPlans = useCallback(() => {
    apiFetch(`${getBackendURL()}/${getSubRoutePath(user)}/merchant/plans?ownerId=${user._id}`)
      .then(response => response.json())
      .then(response => {
        if (response !== null && response.plans.length > 0) {
          let plans = [];
          response.plans.forEach(plan => {
            plans.push({
              id: plan._id,
              name: plan.name,
              price: plan.price,
              numberOfMonths: plan.numberOfMonths,
            });
          });
          setPlans(plans);
        } else {
          setPlans([]);
        }
      })
      .catch(error => {
        console.log(error);
        setPlans([]);
      });
  }, [user]);

  const fetchSettings = useCallback(() => {
    apiFetch(`${getBackendURL()}/admin/settings`)
      .then(res => res.json())
      .then(res => setSettings(res))
      .catch(err => console.error('Error fetching settings:', err));
  }, []);

  const fetchUsers = useCallback(() => {
    setIsLoading(true);
    apiFetch(
      `${getBackendURL()}/${getSubRoutePath(
        user
      )}/merchant/merchants?ownerId=${user._id}&search=${search}&page=${page}&limit=${limit}`
    )
      .then(response => response.json())
      .then(response => {
        if (
          response !== null &&
          response.merchants &&
          response.merchants.length > 0
        ) {
          let users = [];
          response.merchants.forEach(merchant => {
            console.log(merchant);
            users.push({
              id: merchant._id,
              name: merchant.displayName || (merchant.email && merchant.email.split('@')[0]),
              email: merchant.email,
              phone: merchant.phone,
              category: merchant.category,
              pinCode: merchant.pinCode,
              status: merchant.status,
              paymentStatus: merchant.paymentStatus || 'PAID',
              expiryDate: merchant.planExpiryDate,
              addedBy: merchant.addedBy,
              pendingPaymentType: merchant.pendingPaymentType
            });
          });
          setUsers(users);
          setTotal(response.total);
        } else {
          setUsers([]);
          setTotal(0);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.log(error);
        setUsers([]);
        setTotal(0);
        setIsLoading(false);
      });
  }, [user, search, page, limit]);

  useEffect(() => {
    fetchUsers();
    fetchPlans();
    fetchSettings();
  }, [fetchUsers, fetchPlans, fetchSettings]);

  const onUpdateStatus = index => {
    let userToUpdateStatus = users[index];
    setSelectedUser(userToUpdateStatus);
    setInProgress(false);
    setIsRequestCompleted(false);
    setActionStatus('');
    setError('');
    setIsStatusUpdateRequest(true);
    onOpen();
  };

  const onUpdatePlan = index => {
    let userToUpdatePlan = users[index];
    setSelectedUser(userToUpdatePlan);
    setInProgress(false);
    setIsRequestCompleted(false);
    setActionStatus('');
    setError('');
    setIsStatusUpdateRequest(false);
    setPaymentMethod('OFFLINE');
    setTransactionId('');
    onOpen();
  };

  const updateUserPlan = () => {
    if (!selectedPlan) {
      setError('Please select the plan');
      setIsRequestCompleted(true);
      return;
    } else if (paymentMethod === 'OFFLINE' && transactionId === '') {
      setError('Please add the transactionId');
      setIsRequestCompleted(true);
      return;
    }
    setError('');
    setIsRequestCompleted(false);
    setInProgress(true);
    apiFetch(`${getBackendURL()}/${getSubRoutePath(user)}/merchant/merchants/plan/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: selectedUser.id,
        planId: selectedPlan.id,
        txnId: transactionId,
        ownerId: user._id,
        paymentMethod: paymentMethod,
      }),
    })
      .then(response => response.json())
      .then(response => {
        if (response.updated) {
          setActionStatus(response.message);
        } else {
          setError(response.message);
        }
        setSelectedUser({});
        setSelectedPlan({});
        setInProgress(false);
        setIsRequestCompleted(true);
        fetchUsers();
      })
      .catch(error => {
        setSelectedUser({});
        setSelectedPlan({});
        setInProgress(false);
        setError(error);
      });
  };

  const updateUserStatus = () => {
    setError('');
    setInProgress(true);
    apiFetch(`${getBackendURL()}/${getSubRoutePath(user)}/merchant/merchants/toggle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: selectedUser.id,
        ownerId: user._id,
        status: !selectedUser.status,
      }),
    })
      .then(response => response.json())
      .then(response => {
        if (response.updated) {
          setActionStatus(response.message);
        } else {
          setError(response.message);
        }
        setSelectedUser({});
        setInProgress(false);
        setIsRequestCompleted(true);
        fetchUsers();
      })
      .catch(error => {
        console.log(error);
        setSelectedUser({});
        setInProgress(false);
        setError(error);
      });
  };

  const setPlan = id => {
    if (!id) {
      setSelectedPlan(null);
      return;
    }
    let plan = plans.find(plan => plan.id === id);
    setSelectedPlan(plan);
  };

  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/">
            Home
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/merchants">
            Merchants
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4}>
        <Flex flex={1}>
          <Box w={'100%'}>
            <Card bg={useColorModeValue('white', 'gray.700')}>
              <CardHeader>
                <Flex spacing="4" alignItems="center" flexWrap="wrap">
                  <Heading size="md" mb={{ base: 2, md: 0 }}>
                    Merchants
                  </Heading>
                  <Input
                    placeholder="Search name or email..."
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    mx={{ base: 0, md: 4 }}
                    maxW="300px"
                    size="sm"
                  />
                </Flex>
              </CardHeader>
              <Divider />
              <CardBody>
                <TableContainer data-testid="users-table">
                  {isLoading ? (
                    <Flex justify="center" align="center" minH="200px">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="blue.500"
                        size="xl"
                      />
                    </Flex>
                  ) : (
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Sr. No</Th>
                          <Th>Name</Th>
                          <Th>Category</Th>
                          <Th>Pin Code</Th>
                          <Th>Status</Th>
                          <Th>Payment Status</Th>
                          <Th>Expiry Date</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {users.map((user, index) => {
                          return (
                            <Tr key={user.id}>
                              <Td>{(page - 1) * limit + index + 1}</Td>
                              <Td>
                                {user.name}
                                {user.addedBy && user.addedBy !== 'Unknown' && (
                                  <Tooltip label={`This merchant was added by ${user.addedBy}`} aria-label="Added By Tooltip">
                                    <Tag ml={2} colorScheme={user.addedBy === 'Self' ? 'blue' : (user.addedBy === 'Distributor' ? 'purple' : (user.addedBy === 'Support' ? 'orange' : 'teal'))} variant="outline" size="sm" borderRadius="full">
                                      {user.addedBy}
                                    </Tag>
                                  </Tooltip>
                                )}
                              </Td>
                              <Td isNumeric>{user.category}</Td>
                              <Td isNumeric>{user.pinCode}</Td>
                              <Td>
                                <Badge
                                  colorScheme={user.status ? 'green' : 'red'}
                                  borderRadius="full"
                                >
                                  {user.status ? 'Active' : 'Disabled'}
                                </Badge>
                              </Td>
                              <Td>
                                <Tag
                                  size="sm"
                                  colorScheme={
                                    user.paymentStatus?.toLowerCase() === 'paid' ? 'green' :
                                      user.paymentStatus?.toLowerCase() === 'pending' ? 'yellow' : 'red'
                                  }
                                  variant="subtle"
                                >
                                  <TagLabel>
                                    {user.paymentStatus?.toLowerCase() === 'pending' && user.pendingPaymentType === 'UPGRADE'
                                      ? 'Pending (Upgrade)'
                                      : user.paymentStatus}
                                  </TagLabel>
                                </Tag>
                              </Td>
                              <Td>{new Date(user.expiryDate).toLocaleDateString()}</Td>
                              <Td>
                                <Stack direction={{ base: 'column' }} spacing={1}>
                                  {!(user.paymentStatus?.toLowerCase() === 'pending' && user.pendingPaymentType === 'UPGRADE') && (
                                    <Button
                                      size="xs"
                                      colorScheme={'teal'}
                                      variant="outline"
                                      onClick={() => onUpdatePlan(index)}
                                    >
                                      Update Plan
                                    </Button>
                                  )}
                                  <Button
                                    size="xs"
                                    colorScheme={user.status ? 'red' : 'green'}
                                    variant="outline"
                                    onClick={() => onUpdateStatus(index)}
                                  >
                                    {user.status ? 'Deactivate' : 'Activate'}
                                  </Button>
                                  {(user.paymentStatus?.toLowerCase() === 'pending' || user.paymentStatus?.toLowerCase() === 'failed') && (
                                    <>
                                      <Button
                                        size="xs"
                                        colorScheme="blue"
                                        onClick={() => {
                                          const txnId = window.prompt('Enter manual Transaction ID:');
                                          if (txnId) markMerchantAsPaid(user.id, txnId);
                                        }}
                                      >
                                        Mark Paid
                                      </Button>
                                      <Button
                                        size="xs"
                                        colorScheme="purple"
                                        variant="outline"
                                        onClick={() => openResendConfirm(user)}
                                      >
                                        Resend Link
                                      </Button>
                                    </>
                                  )}
                                </Stack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  )}
                </TableContainer>
              </CardBody>
              <Divider />
              <Flex p={4} justify="space-between" align="center">
                <Text fontSize="sm" color="gray.500">
                  Showing {total > 0 ? (page - 1) * limit + 1 : 0} to{' '}
                  {Math.min(page * limit, total)} of {total} results
                </Text>
                <Flex gap={2}>
                  <Button
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page * limit >= total}
                  >
                    Next
                  </Button>
                </Flex>
              </Flex>
            </Card>
          </Box>
        </Flex>
      </Stack>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        closeOnOverlayClick={false}
        size="lg"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {isStatusUpdateRequest ? 'Update status' : 'Update Plan'}
            </AlertDialogHeader>
            {inProgress ? (
              <Box ml={'auto'} mr={'auto'} mt={3} mb={3}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Box>
            ) : (
              <>
                {isStatusUpdateRequest ? (
                  <>
                    {isRequestCompleted ? (
                      <>
                        <AlertDialogBody m={3}>
                          <Alert
                            status="success"
                            display={actionStatus ? 'flex' : 'none'}
                          >
                            <AlertIcon />
                            {actionStatus}
                          </Alert>
                          <Alert
                            status="error"
                            display={error ? 'flex' : 'none'}
                          >
                            <AlertIcon />
                            {error}
                          </Alert>
                        </AlertDialogBody>
                        <AlertDialogFooter justifyContent={'center'}>
                          <Button ref={cancelRef} onClick={onClose}>
                            {error ? 'Retry' : 'Go back'}
                          </Button>
                        </AlertDialogFooter>
                      </>
                    ) : (
                      <>
                        <AlertDialogBody>
                          Are you sure you want to{' '}
                          {selectedUser.status ? 'Deactivate' : 'Activate'}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                          <Button ref={cancelRef} onClick={onClose}>
                            Cancel
                          </Button>
                          <Button
                            colorScheme={selectedUser.status ? 'red' : 'green'}
                            onClick={updateUserStatus}
                            ml={3}
                          >
                            {selectedUser.status ? 'Deactivate' : 'Activate'}
                          </Button>
                        </AlertDialogFooter>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {isRequestCompleted ? (
                      <>
                        <AlertDialogBody m={3}>
                          <Alert
                            status="success"
                            display={actionStatus ? 'flex' : 'none'}
                          >
                            <AlertIcon />
                            {actionStatus}
                          </Alert>
                          <Alert
                            status="error"
                            display={error ? 'flex' : 'none'}
                          >
                            <AlertIcon />
                            {error}
                          </Alert>
                        </AlertDialogBody>
                        <AlertDialogFooter justifyContent={'center'}>
                          <Button ref={cancelRef} onClick={onClose}>
                            {error ? 'Retry' : 'Go back'}
                          </Button>
                        </AlertDialogFooter>
                      </>
                    ) : (
                      <>
                        <AlertDialogBody>
                          <Stack spacing={4}>
                            <FormControl isRequired>
                              <FormLabel>Plan Name</FormLabel>
                              <Select
                                placeholder="Select Plan"
                                onChange={event => setPlan(event.target.value)}
                                value={selectedPlan?.id || ''}
                              >
                                {plans.map(plan => {
                                  return (
                                    <option key={plan.id} value={plan.id}>
                                      {plan.name} (Rs.{plan.price})
                                    </option>
                                  );
                                })}
                              </Select>
                            </FormControl>
                            <FormControl isRequired>
                              <FormLabel>Payment Method</FormLabel>
                              <RadioGroup onChange={setPaymentMethod} value={paymentMethod}>
                                <Stack direction="row" spacing={5}>
                                  <Radio value="OFFLINE">Offline</Radio>
                                  <Radio value="ONLINE" isDisabled={settings?.paymentGatewayEnabled === false}>Online</Radio>
                                </Stack>
                              </RadioGroup>
                            </FormControl>

                            {paymentMethod === 'OFFLINE' && (
                              <>
                                <FormControl isRequired>
                                  <FormLabel>Transaction ID</FormLabel>
                                  <Input
                                    type="text"
                                    value={transactionId}
                                    onChange={event => setTransactionId(event.target.value)}
                                    placeholder="Enter offline transaction ID"
                                  />
                                </FormControl>

                                {selectedPlan && (
                                  <Box textAlign="center" p={4} border="1px dashed" borderColor="gray.300" borderRadius="md">
                                    <Text fontWeight="bold" mb={2}>Payment QR Code for {selectedPlan.name}</Text>
                                    <Flex justify="center">
                                      <Box bg="white" p={2} borderRadius="md" boxShadow="sm">
                                        <img
                                          src={settings?.qrCodePath}
                                          alt="Payment QR Code"
                                          width="200"
                                          height="200"
                                        />
                                      </Box>
                                    </Flex>
                                    <Text fontSize="xs" mt={2} color="gray.500">Scan this QR to pay Rs.{selectedPlan.price}</Text>
                                  </Box>
                                )}
                              </>
                            )}

                            {paymentMethod === 'ONLINE' && (
                              <Box p={3} bg="blue.50" borderRadius="md">
                                <Text fontSize="sm" color="blue.700">A payment link will be sent to the merchant's email.</Text>
                              </Box>
                            )}
                          </Stack>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                          <Button ref={cancelRef} onClick={onClose}>
                            Cancel
                          </Button>
                          <Button
                            colorScheme={'teal'}
                            onClick={updateUserPlan}
                            ml={3}
                          >
                            Update Plan
                          </Button>
                        </AlertDialogFooter>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog >
      <AlertDialog
        isOpen={!!resendConfirmUser}
        leastDestructiveRef={resendCancelRef}
        onClose={() => !resendInProgress && setResendConfirmUser(null)}
        closeOnOverlayClick={false}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Resend Payment Link
            </AlertDialogHeader>
            {resendInProgress ? (
              <Box ml="auto" mr="auto" mt={6} mb={6}>
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="purple.500" size="xl" />
              </Box>
            ) : (
              <>
                <AlertDialogBody>
                  Are you sure you want to resend the payment link to{' '}
                  <strong>{resendConfirmUser?.name}</strong>?
                  <br />
                  If the existing link has expired, a new one will be generated automatically.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={resendCancelRef} onClick={() => setResendConfirmUser(null)}>
                    Cancel
                  </Button>
                  <Button colorScheme="purple" onClick={confirmResendMerchantPaymentLink} ml={3}>
                    Yes, Resend
                  </Button>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default MerchantUsersPage;
